/**
 * useEditorState Hook
 * 
 * Manages the video editor state with undo/redo support.
 * Uses useReducer for predictable state updates.
 */

import { useReducer, useCallback, useMemo } from 'react';
import {
  EditorClip,
  EditorState,
  HistoryState,
  HistoryAction,
} from '../types';
import { generateClipId, calculateTotalDuration, getClipSortKey } from '../utils/clipUtils';

// ============================================================================
// History Reducer (Undo/Redo)
// ============================================================================

const MAX_HISTORY_SIZE = 50;

const insertClipSorted = (
  clips: EditorClip[],
  clip: EditorClip
): { clips: EditorClip[]; insertedId: string } => {
  const newClip = { ...clip, id: clip.id || generateClipId() };
  const newClips = [...clips];
  const clipKey = getClipSortKey(newClip);

  let insertIndex = newClips.findIndex((c) => getClipSortKey(c) > clipKey);
  if (insertIndex === -1) {
    newClips.push(newClip);
  } else {
    newClips.splice(insertIndex, 0, newClip);
  }

  return { clips: newClips, insertedId: newClip.id };
};

function historyReducer(state: HistoryState, action: HistoryAction): HistoryState {
  switch (action.type) {
    case 'SET': {
      // Don't add to history if clips are the same
      if (JSON.stringify(state.present) === JSON.stringify(action.clips)) {
        return state;
      }
      const newPast = [...state.past, state.present].slice(-MAX_HISTORY_SIZE);
      return {
        past: newPast,
        present: action.clips,
        future: [],
      };
    }
    case 'UNDO': {
      if (state.past.length === 0) return state;
      const previous = state.past[state.past.length - 1];
      const newPast = state.past.slice(0, -1);
      return {
        past: newPast,
        present: previous,
        future: [state.present, ...state.future],
      };
    }
    case 'REDO': {
      if (state.future.length === 0) return state;
      const next = state.future[0];
      const newFuture = state.future.slice(1);
      return {
        past: [...state.past, state.present],
        present: next,
        future: newFuture,
      };
    }
    case 'CLEAR':
      return {
        past: [],
        present: [],
        future: [],
      };
    default:
      return state;
  }
}

// ============================================================================
// Editor State Reducer
// ============================================================================

interface InternalState {
  selectedClipId: string | null;
  playheadPosition: number;
  isPlaying: boolean;
  zoomLevel: number;
}

type InternalAction =
  | { type: 'SELECT_CLIP'; clipId: string | null }
  | { type: 'SET_PLAYHEAD'; position: number }
  | { type: 'SET_PLAYING'; isPlaying: boolean }
  | { type: 'SET_ZOOM'; level: number };

function internalReducer(state: InternalState, action: InternalAction): InternalState {
  switch (action.type) {
    case 'SELECT_CLIP':
      return { ...state, selectedClipId: action.clipId };
    case 'SET_PLAYHEAD':
      return { ...state, playheadPosition: action.position };
    case 'SET_PLAYING':
      return { ...state, isPlaying: action.isPlaying };
    case 'SET_ZOOM':
      return { ...state, zoomLevel: Math.max(0.5, Math.min(4, action.level)) };
    default:
      return state;
  }
}

// ============================================================================
// Main Hook
// ============================================================================

export interface UseEditorStateReturn {
  state: EditorState;
  // Clip operations
  addClip: (clip: EditorClip) => void;
  addClips: (clips: EditorClip[]) => void;
  removeClip: (clipId: string) => void;
  reorderClips: (clipIds: string[]) => void;
  updateClip: (clipId: string, updates: Partial<EditorClip>) => void;
  trimClip: (clipId: string, startTime: number, endTime: number) => void;
  splitClip: (clipId: string, splitTime: number) => void;
  clearAll: () => void;
  loadProject: (clips: EditorClip[]) => void;
  // Selection & playback
  selectClip: (clipId: string | null) => void;
  setPlayhead: (position: number) => void;
  setPlaying: (isPlaying: boolean) => void;
  setZoom: (level: number) => void;
  // History
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
}

export function useEditorState(initialClips: EditorClip[] = []): UseEditorStateReturn {
  // History state (clips with undo/redo)
  const [history, dispatchHistory] = useReducer(historyReducer, {
    past: [],
    present: initialClips,
    future: [],
  });

  // Internal state (selection, playhead, etc. - no undo/redo)
  const [internal, dispatchInternal] = useReducer(internalReducer, {
    selectedClipId: null,
    playheadPosition: 0,
    isPlaying: false,
    zoomLevel: 1,
  });

  // Computed values
  const totalDuration = useMemo(
    () => calculateTotalDuration(history.present),
    [history.present]
  );

  // Combined state
  const state: EditorState = useMemo(
    () => ({
      clips: history.present,
      selectedClipId: internal.selectedClipId,
      playheadPosition: internal.playheadPosition,
      isPlaying: internal.isPlaying,
      zoomLevel: internal.zoomLevel,
      totalDuration,
    }),
    [history.present, internal, totalDuration]
  );

  // ============================================================================
  // Clip Operations
  // ============================================================================

  const addClip = useCallback((clip: EditorClip) => {
    const { clips: newClips, insertedId } = insertClipSorted(history.present, clip);
    dispatchHistory({ type: 'SET', clips: newClips });
    dispatchInternal({ type: 'SELECT_CLIP', clipId: insertedId });
  }, [history.present]);

  const addClips = useCallback((clips: EditorClip[]) => {
    let updatedClips = history.present;
    let lastInsertedId: string | null = null;

    clips.forEach((clip) => {
      const result = insertClipSorted(updatedClips, clip);
      updatedClips = result.clips;
      lastInsertedId = result.insertedId;
    });

    dispatchHistory({ type: 'SET', clips: updatedClips });
    if (lastInsertedId) {
      dispatchInternal({ type: 'SELECT_CLIP', clipId: lastInsertedId });
    }
  }, [history.present]);

  const removeClip = useCallback((clipId: string) => {
    const newClips = history.present.filter((c) => c.id !== clipId);
    dispatchHistory({ type: 'SET', clips: newClips });
    // Clear selection if removed clip was selected
    if (internal.selectedClipId === clipId) {
      dispatchInternal({ type: 'SELECT_CLIP', clipId: null });
    }
  }, [history.present, internal.selectedClipId]);

  const reorderClips = useCallback((clipIds: string[]) => {
    const clipMap = new Map(history.present.map((c) => [c.id, c]));
    const newClips = clipIds
      .map((id) => clipMap.get(id))
      .filter((c): c is EditorClip => c !== undefined);
    dispatchHistory({ type: 'SET', clips: newClips });
  }, [history.present]);

  const updateClip = useCallback((clipId: string, updates: Partial<EditorClip>) => {
    const newClips = history.present.map((c) =>
      c.id === clipId ? { ...c, ...updates } : c
    );
    dispatchHistory({ type: 'SET', clips: newClips });
  }, [history.present]);

  const trimClip = useCallback((clipId: string, startTime: number, endTime: number) => {
    const newClips = history.present.map((c) =>
      c.id === clipId ? { ...c, startTime, endTime } : c
    );
    dispatchHistory({ type: 'SET', clips: newClips });
  }, [history.present]);

  const splitClip = useCallback((clipId: string, splitTime: number) => {
    const clipIndex = history.present.findIndex((c) => c.id === clipId);
    if (clipIndex === -1) return;

    const clip = history.present[clipIndex];

    // splitTime is relative to the clip's start on timeline
    // Convert to absolute time in source video
    const absoluteSplitTime = clip.startTime + splitTime;

    // Validate split point
    if (absoluteSplitTime <= clip.startTime || absoluteSplitTime >= clip.endTime) {
      return;
    }

    const clip1: EditorClip = {
      ...clip,
      id: generateClipId(),
      endTime: absoluteSplitTime,
      label: clip.label ? `${clip.label} (1)` : undefined,
    };

    const clip2: EditorClip = {
      ...clip,
      id: generateClipId(),
      startTime: absoluteSplitTime,
      label: clip.label ? `${clip.label} (2)` : undefined,
    };

    const newClips = [
      ...history.present.slice(0, clipIndex),
      clip1,
      clip2,
      ...history.present.slice(clipIndex + 1),
    ];

    dispatchHistory({ type: 'SET', clips: newClips });
  }, [history.present]);

  const clearAll = useCallback(() => {
    dispatchHistory({ type: 'CLEAR' });
    dispatchInternal({ type: 'SELECT_CLIP', clipId: null });
    dispatchInternal({ type: 'SET_PLAYHEAD', position: 0 });
  }, []);

  const loadProject = useCallback((clips: EditorClip[]) => {
    dispatchHistory({ type: 'SET', clips });
    dispatchInternal({ type: 'SELECT_CLIP', clipId: null });
    dispatchInternal({ type: 'SET_PLAYHEAD', position: 0 });
  }, []);

  // ============================================================================
  // Selection & Playback
  // ============================================================================

  const selectClip = useCallback((clipId: string | null) => {
    dispatchInternal({ type: 'SELECT_CLIP', clipId });
  }, []);

  const setPlayhead = useCallback((position: number) => {
    dispatchInternal({ type: 'SET_PLAYHEAD', position: Math.max(0, position) });
  }, []);

  const setPlaying = useCallback((isPlaying: boolean) => {
    dispatchInternal({ type: 'SET_PLAYING', isPlaying });
  }, []);

  const setZoom = useCallback((level: number) => {
    dispatchInternal({ type: 'SET_ZOOM', level });
  }, []);

  // ============================================================================
  // History
  // ============================================================================

  const undo = useCallback(() => {
    dispatchHistory({ type: 'UNDO' });
  }, []);

  const redo = useCallback(() => {
    dispatchHistory({ type: 'REDO' });
  }, []);

  return {
    state,
    addClip,
    addClips,
    removeClip,
    reorderClips,
    updateClip,
    trimClip,
    splitClip,
    clearAll,
    loadProject,
    selectClip,
    setPlayhead,
    setPlaying,
    setZoom,
    undo,
    redo,
    canUndo: history.past.length > 0,
    canRedo: history.future.length > 0,
  };
}

export default useEditorState;

